import { useApolloClient } from "@apollo/client";
import {
  Text,
  useMantineTheme,
  ActionIcon,
  MantineTheme,
  Button,
} from "@mantine/core";

import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "src/api/AppContext";
import {
  ROUTE_NAMES,
  DEFAULT_ORDER,
  EMPLOYEE_ROLE_OBJ,
} from "src/utils/constants";
import { useModals } from "@mantine/modals";
import { MdDelete } from "react-icons/md";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import {
  GetEmployeeRolePageQuery,
  GetEmployeeRolePageQueryVariables,
  useDeleteEmployeeRoleMutation,
  useIsRoleAssignedLazyQuery,
} from "src/generated/graphql";
import { GetEmployeeRolePage } from "src/api/graphql/queries/EmployeeRole";
import { FaCheckCircle } from "react-icons/fa";
import RoleForm from "./RoleForm";
import NewRoleForm from "./RoleForm/NewRoleForm";
import useWritePermission from "src/hooks/usePermission";

export type EmployeeRole = ArrayElement<
  GetEmployeeRolePageQuery["employeeRolePage"]["items"]
>;

const Roles = () => {
  const modals = useModals();
  const { setCurrentPageTitle } = useContext(AppContext);
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgRoles);
  const theme = useMantineTheme();
  const { query } = useApolloClient();
  const [refetchTableData, setrefetchTableData] = useState(false);

  const getEmployeeRoles = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<EmployeeRole>> => {
    let { data } = await query<
      GetEmployeeRolePageQuery,
      GetEmployeeRolePageQueryVariables
    >({
      query: GetEmployeeRolePage,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.name,
          filters: fetchParams.filters,
        },
      },
    });

    if (data.employeeRolePage) {
      return {
        rowCount: data.employeeRolePage.totalRows,
        rowData: data.employeeRolePage.items as EmployeeRole[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  const [deleteEmployeeRoleEntry] = useDeleteEmployeeRoleMutation({
    onCompleted: () => {
      setrefetchTableData(!refetchTableData);
    },
  });

  const [isRoleAssigned, { loading: checkLoading }] =
    useIsRoleAssignedLazyQuery({
      fetchPolicy: "network-only",
    });

  const onDelete = async (roleId: string, name: string) => {
    const success = await isRoleAssigned({ variables: { roleId } });
    if (!success.data?.isRoleAssigned) {
      modals.openContextModal("delete", {
        closeOnClickOutside: false,
        title: `Confirm Delete`,
        centered: true,
        innerProps: {
          innerText: `Are you sure you want to delete the selected role ${name}?`,
          onDelete: () =>
            deleteEmployeeRoleEntry({
              variables: { deleteEmployeeRoleId: roleId },
            }),
        },
      });
    } else {
      modals.openModal({
        closeOnClickOutside: true,
        title: "Update Role",
        children: (
          <NewRoleForm
            roleId={roleId}
            setrefetchTableData={setrefetchTableData}
            refetchTableData={refetchTableData}
          />
        ),
      });
    }
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.TIME_CLOCK);
    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleCreate = useCallback(
    (empRoleId?: string, seedId?: string) => {
      modals.openModal({
        closeOnClickOutside: false,
        title: `${empRoleId ? "Update" : "Create"} Custom Role`,
        children: (
          <RoleForm
            empRoleId={empRoleId}
            setrefetchTableData={setrefetchTableData}
            refetchTableData={refetchTableData}
            isAllowWrite={isAllowWrite}
            seedId={seedId}
          />
        ),
      });
    },
    [refetchTableData, isAllowWrite]
  );

  const ShowTable = useCallback(
    ({ theme }: { theme: MantineTheme }) => {
      return (
        <div>
          <ScrollableDataGrid<EmployeeRole>
            columnConfigs={[
              {
                field: "name",
                name: "Name",
                filter: {
                  field: "name",
                  type: "text",
                },
                sortable: true,
                cellRenderer: (rowItem) => (
                  <Text
                    weight={500}
                    onClick={() => handleCreate(rowItem.id, rowItem.seedId!)}
                    style={{ cursor: "pointer" }}
                  >
                    {rowItem.name}
                  </Text>
                ),
              },
              {
                field: "seedId",
                name: "Default",
                sortable: true,
                cellRenderer: ({ seedId }) => (
                  <FaCheckCircle
                    size={24}
                    color={seedId ? "green" : "#ddd"}
                    style={{
                      opacity: seedId ? 1 : 0,
                    }}
                  />
                ),
              },
              ...(isAllowWrite
                ? [
                    {
                      field: "",
                      name: "",
                      cellRenderer: ({ id, seedId, name }: EmployeeRole) => (
                        <>
                          {!seedId && (
                            <ActionIcon
                              onClick={() => onDelete(id, name)}
                              style={{
                                position: "initial",
                              }}
                              loading={checkLoading}
                            >
                              <MdDelete
                                color={`${theme.colors.red[6]}`}
                                size={23}
                              />
                            </ActionIcon>
                          )}
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
            dataFetch={getEmployeeRoles}
          />
        </div>
      );
    },
    [refetchTableData, isAllowWrite]
  );

  return (
    <>
      {isAllowWrite && (
        <Button onClick={() => handleCreate()} mb={"xs"}>
          Create +
        </Button>
      )}

      <ShowTable theme={theme} />
    </>
  );
};

export default Roles;
